import React from 'react';
import { createPortal } from 'react-dom';
import { ui } from '../../../config/uiSizes';
import campaignManagementImg from '../../../assets/campaign-management-screenshot.png';
import manualRollbackExampleImg from '../../../assets/manual-rollback-example.png';
import { ImageHoverPreview } from '../Shared/ImageHoverPreview';

interface SnapshotCompToolFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalView = 'main' | 'rollback';

// ── Bullet list item helper ─────────────────────────────────────────────────

const BulletItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={`flex items-start ${ui.gap.xs}`}>
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
    <span className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>{children}</span>
  </li>
);

// ── Divider ─────────────────────────────────────────────────────────────────

const Divider: React.FC = () => (
  <hr className="border-gray-200 dark:border-gray-700 my-4" />
);

// ── Section heading ──────────────────────────────────────────────────────────

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={`${ui.text.label} font-bold text-gray-900 dark:text-gray-100 mb-2`}>
    {children}
  </p>
);

// ── Accent paragraph (blue intro text) ────────────────────────────────────

const AccentParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={`${ui.text.body} text-blue-600 dark:text-blue-400 mb-2 leading-relaxed`}>
    {children}
  </p>
);

// ── RepliCloud-style FAQ card ─────────────────────────────────────────────

interface FaqCardItem {
  question: string;
  icon: React.ReactNode;
  accent: string;
  answer: React.ReactNode;
}

const FaqCard: React.FC<FaqCardItem> = ({ question, icon, accent, answer }) => {
  const borderCls = accent.split(' ').filter(c => c.includes('border')).join(' ');
  const bgCls = accent.split(' ').filter(c => c.includes('bg')).join(' ');
  const textCls = accent.split(' ').filter(c => c.includes('text')).join(' ');
  return (
    <div className={`rounded-xl border ${borderCls} bg-gray-50 dark:bg-gray-800/60 overflow-hidden`}>
      <div className={`flex items-center ${ui.gap.md} px-4 py-3 ${bgCls}`}>
        <div className={`flex-shrink-0 ${textCls}`}>{icon}</div>
        <p className={`${ui.text.label} font-bold text-gray-900 dark:text-gray-100`}>{question}</p>
      </div>
      <div className={`px-4 py-3 ${ui.text.body} text-gray-700 dark:text-gray-300 leading-relaxed`}>
        {answer}
      </div>
    </div>
  );
};

// ── Rollback FAQ card data ────────────────────────────────────────────────

const rollbackFaqs: FaqCardItem[] = [
  {
    question: 'What does a migration do?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'A migration applies selected Genesys Cloud objects and resources from a source environment into your target Genesys Cloud organization.'
  },
  {
    question: 'What happens during a migration?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">When a migration runs, the following may occur:</p>
        <ul className="space-y-1.5 mb-2">
          {[
            'Selected resources are copied into the target environment',
            'Existing resources can be modified if transformations are applied during the migration process',
            'Relationships and dependencies between resources may change'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-500 dark:text-gray-400 italic">These updates are applied directly to your target environment once the migration executes.</p>
      </>
    )
  },
  {
    question: 'Does RepliCloud create a backup before a migration?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-1">Yes. Before any migration runs, RepliCloud automatically captures a snapshot of your target organization.</p>
        <p className="text-gray-500 dark:text-gray-400 italic">This snapshot serves as a point-in-time reference of your environment before any changes are applied.</p>
      </>
    )
  },
  {
    question: 'What is a snapshot?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'A snapshot is a backup of your Genesys Cloud configuration at a specific moment in time, captured just before a migration executes.'
  },
  {
    question: 'What can I do with a snapshot?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">You can download snapshot files and use them to:</p>
        <ul className="space-y-1.5">
          {[
            'Review your configuration prior to the migration',
            'Manually restore or recreate settings in Genesys Cloud if needed'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    question: 'What are transformations?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: 'Transformations let you review and adjust how configurations will be applied to the selected resources before the migration runs, ensuring changes align with your needs before the resources are imported.'
  },
  {
    question: 'How does rollback work today?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2 font-medium text-gray-700 dark:text-gray-300">Rollback is currently a manual process.</p>
        <ul className="space-y-1.5">
          {[
            'A snapshot is automatically created before each migration',
            'If the migration results are not as expected, the snapshot provides a reliable reference for restoring your environment',
            'You must manually revert changes in Genesys Cloud using the snapshot data'
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    question: 'Who should perform a rollback?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <>
        <p className="mb-2">Manual rollback is intended for:</p>
        <ul className="space-y-1.5">
          {['Genesys Cloud administrators', 'Developers familiar with managing configurations'].map(
            (item) => (
              <li key={item} className="flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{item}</span>
              </li>
            )
          )}
        </ul>
      </>
    )
  },
  {
    question: 'What are best practices for manual rollback?',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <ul className="space-y-1.5">
        {[
          'Use the snapshot as your source of truth for restoration',
          null,
          'Additional reference can be found in ReadMe included in the downloaded migration files.'
        ].map((item, i) =>
          item === null ? (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>
                For a quick and clear understanding of what changed, click <strong>Review Target Organization Changes</strong> to access the Snapshot Comparison
                Tool, accessible from the Migration Details page. The Snapshot Comparison Tool provides an in-product comparison of pre- and post-migration snapshots
                of the Target organization.
              </span>
            </li>
          ) : item === 'Additional reference can be found in ReadMe included in the downloaded migration files.' ? (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>
                Additional reference can be found in <strong>ReadMe</strong> included in the downloaded migration files.
              </span>
            </li>
          ) : (
            <li key={i} className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{item}</span>
            </li>
          )
        )}
        <li className="flex items-start gap-2">
          <svg className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>After completing manual fixes, take a new snapshot to capture the updated state</span>
        </li>
      </ul>
    )
  },
  {
    question: 'RepliCloud Support',
    icon: (
      <svg className={ui.svg.lg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    accent: 'bg-slate-400/60 dark:bg-slate-600/60 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-gray-700',
    answer: (
      <p>
        Still need help? Email support at{' '}
        <a
          href="mailto:support@edci.com"
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          support@edci.com
        </a>
      </p>
    )
  }
];

// ── Main view content ────────────────────────────────────────────────────────

const MainViewContent: React.FC<{
  onViewRollback: () => void;
  setShowHoverPreview: (show: boolean) => void;
  handleImageHover: (event: React.MouseEvent<HTMLImageElement>, imageSrc: string) => void;
}> = ({ onViewRollback, setShowHoverPreview, handleImageHover }) => {
  return (
    <div>
      {/* Info box */}
      <div className="rounded-lg border border-blue-300 dark:border-blue-800 mb-2">
        <div className="h-1.5 bg-gradient-to-r from-blue-400 to-blue-500" />
        <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <p className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-200 mb-1`}>About this view</p>
          <p className={`${ui.text.body} text-blue-700 dark:text-blue-300 leading-relaxed`}>
            These instructions guide you through using the Snapshot Comparison Tool to understand what
            changed during your migration and manually undo any unexpected changes.
          </p>
        </div>
      </div>

      <Divider />

      <div className="mb-4">
        <SectionHeading>How to Use the Snapshot Comparison Tool</SectionHeading>
        <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
          This screen shows a comparison of the target organization before and after a migration,
          highlighting what was added, changed, or removed as a result of the import.
        </p>
        <ul className="space-y-1.5 ml-2 mb-3">
          <BulletItem>
            Changes are grouped by <strong>resource type and resource name</strong>
          </BulletItem>
          <BulletItem>
            Only <strong>resources that changed are shown</strong>
          </BulletItem>
          <BulletItem>
            Within each resource, only the <strong>properties that changed are displayed</strong>
          </BulletItem>
        </ul>
        <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
          For each change, you&apos;ll see:
        </p>
        <ul className="space-y-1.5 ml-2">
          <BulletItem>
            The <strong>previous value (before migration)</strong>
          </BulletItem>
          <BulletItem>
            The <strong>current value (after migration)</strong>
          </BulletItem>
        </ul>
      </div>

      <Divider />

      <div className="mb-4">
        <SectionHeading>Understanding Change Types</SectionHeading>
        <AccentParagraph><strong>Changes are categorized to help you interpret them quickly:</strong></AccentParagraph>
        <ul className="space-y-1.5 ml-2 mb-3">
          <BulletItem>
            <strong className="text-green-700 dark:text-green-400">Added</strong>
            {' – '}New resources imported during the migration. Added resources show all current properties
          </BulletItem>
          <BulletItem>
            <strong className="text-orange-600 dark:text-orange-400">Modified</strong>
            {' – '}Existing resources that were transformed and show only the properties that changed.
          </BulletItem>
          <BulletItem>
            <strong className="text-red-700 dark:text-red-400">Removed</strong>
            {' – '}Resources or properties that were deleted during the migration. Removed resources or properties
            show their previous state so they can be recreated{' '}
          </BulletItem>
        </ul>
      </div>

      <Divider />

      <div className="mb-4">
        <SectionHeading>Understanding How Transformations are Displayed</SectionHeading>
        <AccentParagraph>
          Transformations are shown only through their impact on the target org—if they cause a visible change, they appear as differences.
        </AccentParagraph>
        <ul className="space-y-1.5 ml-2">
          <BulletItem>
            Some transformations may not appear in this comparison because they didn't result in a change between the pre‑ and post‑migration
            snapshots of the Target Organization.
          </BulletItem>
          <BulletItem>
            This can happen when a transformation results in the <strong>same final value</strong> as what already existed in the target organization. The system
            is working as intended — the comparison highlights <strong>observable differences</strong>, not every transformation rule that was evaluated.
          </BulletItem>
          <BulletItem>
            If an object already existed in Genesys and is simply replaced with an identical version during migration, it will not appear as a change in the Snapshot
            Comparison Tool.
          </BulletItem>
        </ul>
      </div>

      <Divider />

      <div className="mb-4">
        <SectionHeading>Need to rollback? How to Use This Screen to Perform a Manual Rollback</SectionHeading>
        <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
          You can use this screen as a guide to manually revert changes made during a migration. If you would like to review more information
          before proceeding,  <strong>click RepliCloud FAQs below</strong> for additional rollback considerations and best practices.
        </p>
        <AccentParagraph><strong>To perform a manual rollback:</strong></AccentParagraph>
        <ol className="list-decimal ml-5 space-y-2 marker:font-bold">
          <li className={`${ui.text.body} text-gray-700 font-semi dark:text-gray-300`}>
            Identify resources marked as <strong className="text-green-700 dark:text-green-400">Added</strong>, <strong className="text-orange-600 dark:text-orange-400">Modified</strong>, or <strong className="text-red-700 dark:text-red-400">Removed</strong> using the Snapshot Comparison Tool.
          </li>
          <li className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>
            Review the <strong>previous values to understand the original state</strong> one resource at a time. If there is a change that is
            unexpected proceed to <strong>Step 3</strong>. Review related resources to <strong>ensure dependencies</strong> are still correctly configured.
            Be prepared to <strong>manually reconnect or reconfigure</strong> dependent objects.
          </li>
          <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
            If the changes are desired, no manual rollback is necessary.
          </p>
          <li className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>
            If you identified unexpected differences in the Target Organization, you can use RepliCloud’s comparison view to assist with a manual rollback.
            <ul className="space-y-1.5 ml-2 mb-3">
              <BulletItem>
                If a resource was <strong>added by mistake</strong>, use the Genesys Cloud menu to locate the Genesys Object to remove it from the Target Organization to
                align to the Snapshot Comparison Tool's <em>Current Value</em> state.
              </BulletItem>
              <BulletItem>
                If a resource or property was <strong>removed by mistake</strong>, use the Genesys Cloud menu to locate the object, and restore it using the <em>Previous
                  Value</em> state shown in RepliCloud’s Snapshot Comparison Tool.
              </BulletItem>
              <BulletItem>
                If a resource was <strong>modified incorrectly</strong>, use the Genesys Cloud UI to update the affected fields by referencing the <em>Previous Value</em> state
                shown in RepliCloud’s Snapshot Comparison Tool.
              </BulletItem>
            </ul>
            <AccentParagraph><strong><em>How to Handle Object Dependencies During Rollback</em></strong></AccentParagraph>
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
              Some objects in Genesys Cloud have <strong>dependencies on other resources.</strong> If a dependent resource (for example, a queue used by a flow) is deleted and then recreated manually,
              it receives a <strong>new unique ID</strong>, and existing dependencies are <strong>not automatically restored.</strong> This means related objects may no longer function as expected until those
              dependencies are manually reconfigured.
            </p>
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
              To restore dependency configurations, you must first identify the dependent object and reestablish its dependencies. For example, if an unexpected difference is identified in a Contact List name,
              you should first remediate the Contact List name in <strong>List Management</strong>. Then, navigate to <strong>Campaign Management</strong>, locate the dependent campaign, and update the campaign settings to select the correct Contact List.

            </p>
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
              See Screenshot below for an example of how to update a campaign's contact list.
            </p>
            <div className="overflow-visible">
              <img
                src={campaignManagementImg}
                alt="Campaign Management screenshot showing how to update contact list"
                onMouseEnter={(event) => handleImageHover(event, campaignManagementImg)}
                onMouseLeave={() => setShowHoverPreview(false)}
                className="mt-3 rounded-lg border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-xl transition-shadow duration-200 w-full max-w-2xl"
              />
            </div>
          </li>
          <li className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>
            As a best practice, if any manual rollback steps were performed within the Genesys Cloud UI, it is recommended that you perform an additional snapshot after
            you resolved any unexpected differences.
          </li>
        </ol>
        <Divider />

        <div>
          <AccentParagraph><strong>Manual Rollback Example</strong></AccentParagraph>
          <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-3`}>
            The following example describes a simple manual rollback scenario. Actual rollback steps may vary depending on the resource and its dependencies.
          </p>

          <div className="bg-gray-50 dark:bg-gray-800/40 rounded-lg px-4 py-3 mb-3 border-l-4 border-blue-400 dark:border-blue-600">
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
              <strong className="text-blue-700 dark:text-blue-400">Scenario:</strong> A user completes a migration that includes updates to queue configurations, including Division assignments. After the migration, they open the Snapshot
              Comparison Tool and review the queue <strong>Sales_Queue_Dev</strong>. The comparison shows an unexpected difference: the Division changed from
              <strong> Demo</strong> to <strong>Development</strong> during migration.
            </p>
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mb-2`}>
              <strong className="text-blue-700 dark:text-blue-400">Action:</strong> Since this change was not intended, the user performs a manual rollback by navigating to Genesys Cloud queue administration, locating
              <strong> Sales_Queue_Dev</strong>, and changing the Division back to <strong>Demo</strong>.
            </p>
            <p className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>
              <strong className="text-blue-700 dark:text-blue-400">Result:</strong> Once saved in Genesys, the queue is restored to its pre-migration state. As a best practice, the user then runs another snapshot in RepliCloud to capture
              the updated post-rollback state.
            </p>
          </div>

          <div className="overflow-visible">
            <img
              src={manualRollbackExampleImg}
              alt="Manual rollback example screenshot showing division change"
              onMouseEnter={(event) => handleImageHover(event, manualRollbackExampleImg)}
              onMouseLeave={() => setShowHoverPreview(false)}
              className="mt-3 rounded-lg border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-xl transition-shadow duration-200 w-full max-w-2xl"
            />
          </div>

          <p className={`${ui.text.body} text-gray-700 dark:text-gray-300 mt-3 mb-1`}>
            <strong>Will the Snapshot Comparison Tool always show the full impact of a rollback?</strong>
          </p>
          <p className={`${ui.text.body} text-gray-700 dark:text-gray-300`}>
            No. The comparison view highlights direct changes, but it may not reveal downstream impacts caused by dependencies. For example, a flow that depends on a
            queue may not appear as changed even if the queue it depends on was removed or altered.
          </p>
        </div>

      </div >

      <Divider />

      <button
        type="button"
        onClick={onViewRollback}
        className={`${ui.text.body} flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline font-medium text-left`}
      >
        <svg className={`${ui.svg.sm} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Need help? RepliCloud FAQs &rarr;
      </button>
    </div >
  );
};

// ── Rollback view content ────────────────────────────────────────────────────

const RollbackViewContent: React.FC = () => (
  <div className="space-y-3">
    {rollbackFaqs.map((faq) => (
      <FaqCard key={faq.question} {...faq} />
    ))}
  </div>
);

// ── Panel component (inline side panel, slides in from the right) ────────────

export const SnapshotCompToolFAQModal: React.FC<SnapshotCompToolFAQModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [view, setView] = React.useState<ModalView>('main');
  const [panelTop, setPanelTop] = React.useState(0);
  const [showHoverPreview, setShowHoverPreview] = React.useState(false);
  const [hoverPreviewSrc, setHoverPreviewSrc] = React.useState(campaignManagementImg);
  const [hoverPosition, setHoverPosition] = React.useState({ x: 0, y: 0 });
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header');
      if (headerEl) {
        const headerBottom = headerEl.getBoundingClientRect().bottom;
        const parentBottom = headerEl.parentElement?.getBoundingClientRect().bottom ?? headerBottom;
        setPanelTop(Math.max(headerBottom, parentBottom));
      }
    };

    if (!isOpen) return;

    measure();

    const headerEl = document.querySelector('header');
    const observer = new ResizeObserver(measure);
    if (headerEl) observer.observe(headerEl);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isOpen]);

  // Scroll to top when view changes
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [view]);

  const handleClose = React.useCallback(() => {
    setView('main');
    setShowHoverPreview(false);
    onClose();
  }, [onClose]);

  const handleImageHover = React.useCallback((event: React.MouseEvent<HTMLImageElement>, imageSrc: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverPosition({ x: rect.right + 10, y: rect.top });
    setHoverPreviewSrc(imageSrc);
    setShowHoverPreview(true);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <aside
        className="fixed right-0 z-40 animate-slide-in flex flex-col bg-white dark:bg-[#0f1720] border-l-2 border-gray-200 dark:border-gray-700 shadow-2xl"
        style={{ top: panelTop, height: `calc(100vh - ${panelTop}px)`, width: 'min(480px, 38vw)' }}
        aria-label="Migration guidance panel"
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
          {view === 'rollback' ? (
            <button
              type="button"
              onClick={() => setView('main')}
              className={`${ui.text.xs} flex-shrink-0 font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors`}
              aria-label="Back to main view"
            >
              &#8592; Back
            </button>
          ) : (
            <span className="flex-shrink-0 w-10" />
          )}
          <h2
            className={`${ui.text.subTitle} font-bold text-gray-900 dark:text-gray-100 text-center flex-1 leading-tight`}
          >
            {view === 'main' ? 'Manual Rollback Guidance' : 'RepliCloud FAQS'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close panel"
            className={`flex-shrink-0 ${ui.page.modalCloseButton} rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <svg className={ui.svg.sm} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {view === 'main' ? (
            <MainViewContent
              onViewRollback={() => setView('rollback')}
              setShowHoverPreview={setShowHoverPreview}
              handleImageHover={handleImageHover}
            />
          ) : (
            <RollbackViewContent />
          )}
        </div>
      </aside>

      {createPortal(
        <ImageHoverPreview show={showHoverPreview} position={hoverPosition} src={hoverPreviewSrc} />,
        document.body
      )}
    </>
  );
};
